import React, { useState, useCallback } from 'react'
import { WbInputControl, WbInput } from '@workbench/react/dist'
import { Button } from 'components'
import classes from './Counter.module.scss'

interface CounterProps {
  id: string
  label: string
  disabled?: boolean
  initialValue?: number
  maxCount?: number
  minCount?: number
  onChange?: (number: number) => void
  inputProps?: JSX.IntrinsicElements['input']
}

export const Counter: React.FunctionComponent<CounterProps> = ({
  id,
  label,
  initialValue = 0,
  maxCount = Infinity,
  minCount = -Infinity,
  disabled = false,
  onChange = () => {},
  ...inputProps
}) => {
  const [count, setCount] = useState(initialValue)
  const changeCount = useCallback(
    (number: number) => {
      setCount(number)
      onChange(number)
    },
    [onChange, setCount]
  )

  const increment = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      changeCount(Math.min(count + 1, maxCount))
    },
    [count, changeCount, maxCount]
  )

  const decrement = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      changeCount(Math.max(count - 1, minCount))
    },
    [count, changeCount, minCount]
  )

  const onChangeCount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10)
    if (!isNaN(value) && !disabled) {
      changeCount(Math.max(Math.min(value, maxCount), minCount))
    }
  }

  return (
    <div className={classes.counter}>
      <Button
        size="small"
        variant="tertiary"
        onClick={decrement}
        className={classes.button}
        disabled={disabled || count === minCount}
      >
        -
      </Button>

      <WbInputControl>
        <WbInput>
          <label htmlFor={id}>{label}</label>
          <input
            onChange={onChangeCount}
            type="text"
            id={id}
            value={count}
            disabled={disabled}
            {...inputProps}
          />
        </WbInput>
      </WbInputControl>
      <Button
        size="small"
        variant="tertiary"
        onClick={increment}
        className={classes.button}
        disabled={disabled || count === maxCount}
      >
        +
      </Button>
    </div>
  )
}
